import { type FunctionComponent, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import { Navigation } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useResponsive, useTrackImpressionEventByIntersection } from "shared/hooks";
import { theme } from "shared/lib/styles";
import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { advertisersHomeV2QueueItemConnection$key } from "scmp-app/queries/__generated__/advertisersHomeV2QueueItemConnection.graphql";

import {
  Container,
  StyledArrowNext,
  StyledArrowPrevious,
  StyledContentItemHomeAdvertiserV2,
  StyledSwiper,
  StyledSwiperSlide,
  SwiperContainer,
  SwiperWrapper,
  Title,
} from "./styles";

type Props = {
  className?: string;
  reference: advertisersHomeV2QueueItemConnection$key;
};

export const HomeV2Advertisers: FunctionComponent<Props> = ({ className, reference }) => {
  const advertisers = useFragment(
    graphql`
      fragment advertisersHomeV2QueueItemConnection on QueueItemConnection {
        edges {
          node {
            ... on Content {
              entityId
              ...homeAdvertiserV2ContentItemContent
            }
          }
        }
      }
    `,
    reference,
  );

  const edges = advertisers?.edges ?? [];
  const listOfArticles = edges.map(item => item.node.entityId).join(",");

  const isTabletUp = useResponsive(theme.breakpoints.up("tablet"), true);

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles,
        page_type: "homepage",
        widget_name: "advertorial_carousel",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (edges.length === 0) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.FromOurAdvertises,
      }}
    >
      <Container className={className} ref={captureTrackImpressionEventTargetElement}>
        <Title>From our advertisers</Title>
        <SwiperContainer>
          <SwiperWrapper>
            <StyledSwiper
              breakpoints={{
                0: {
                  spaceBetween: 32,
                },
              }}
              freeMode={true}
              modules={[Navigation]}
              navigation={{
                disabledClass: "card-swiper-button-disabled",
                nextEl: ".card-swiper-button-next",
                prevEl: ".card-swiper-button-prev",
              }}
              pagination={false}
              slidesOffsetAfter={isTabletUp ? 0 : 20}
              slidesOffsetBefore={isTabletUp ? 0 : 20}
              slidesPerGroupAuto={true}
              slidesPerView="auto"
            >
              {edges.map((item, index) => (
                <StyledSwiperSlide key={index}>
                  <StyledContentItemHomeAdvertiserV2
                    onClick={(entityId, urlAlias) => {
                      sendGA4Tracking({
                        action: "click",
                        category: "recirculation",
                        customized_parameters: {
                          article_id: entityId,
                          destination_url: getAbsoluteUrl(urlAlias),
                          page_type: "homepage",
                          widget_name: "advertorial_carousel",
                        },
                        subcategory: "widget",
                      });
                    }}
                    reference={item.node}
                  />
                </StyledSwiperSlide>
              ))}
            </StyledSwiper>
            <div className="swiper-button card-swiper-button-prev">
              <StyledArrowPrevious />
            </div>
            <div className="swiper-button card-swiper-button-next">
              <StyledArrowNext />
            </div>
          </SwiperWrapper>
        </SwiperContainer>
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeV2Advertisers.displayName = "HomeV2Advertisers";
